export {
    trackAddToBagFromMiniPDP,
    trackAddToWishlistFromMiniPDP,
    trackViewMoreDetails,
    trackAddToBagError,
    trackAddToWishlistError,
    trackGoToBag,
    trackAddToBagNonBasketProduct,
    trackRemoveRecentlyViewedItem,
} from '@ynap/product-details-page';

export const trackSizeGuideOpen = () => ({
    event: 'size and fit - open',
    effect: 'open tool',
    category: {
        primaryCategory: 'size and fit',
        subCategory: 'product details page',
    },
});

export const trackSizeGuideClose = () => ({
    event: 'size and fit - close',
    effect: 'close tool',
    category: {
        primaryCategory: 'size and fit',
        subCategory: 'product details page',
    },
});

export const trackSizeGuideComplete = () => ({
    event: 'size and fit - complete',
    effect: 'complete tool steps',
    category: {
        primaryCategory: 'size and fit',
        subCategory: 'product details page',
    },
});

export const trackSizeSelect = (analytics, sizeSku, sizeLabel, internationalSizes) => ({
    event: 'size selection',
    category: {
        primaryCategory: 'product page',
    },
    item: getItem(analytics, sizeSku, sizeLabel, null, null, internationalSizes),
});

export const trackSizeAndFitSizeSelect = () => ({
    event: 'size and fit - size selection',
    category: {
        primaryCategory: 'size and fit',
        subCategory: 'product details page',
    },
});

export const trackAccordion = (analytics, accordion) => ({
    event: `open product accordion - ${accordion}`,
    effect: 'view more content',
    category: {
        primaryCategory: 'product page',
    },
    item: getItem(analytics),
});

export const trackAccordionLinkClick = (analytics, targetURL) => ({
    event: 'product accordion - link click',
    effect: 'click on a link inside the product accordion',
    category: {
        primaryCategory: 'product page',
        subCategory: `${targetURL}`,
    },
    item: getItem(analytics),
});

export const trackSizeGuide = (analytics) => ({
    event: 'size and fitting guide',
    effect: 'new window',
    category: {
        primaryCategory: 'product page',
        subCategory: 'open window',
    },
    item: getItem(analytics),
});

export const trackImage = (analytics, interaction) => ({
    event: 'browse product images',
    effect: interaction,
    category: {
        primaryCategory: 'product page',
        subCategory: interaction,
    },
    item: getItem(analytics),
});

export const trackAddToWishlist = (analytics, sizeSku, sizeLabel, internationalSizes) => {
    const { category = {} } = analytics.page;
    return {
        event: 'wish list - add item',
        effect: 'add item to wish list',
        category: {
            primaryCategory: category.primaryCategory,
            subCategory: category.subCategory1,
        },
        item: getItem(analytics, sizeSku, sizeLabel, true, null, internationalSizes),
    };
};

export const upsellNavigation = (analytics, recommendedProduct, position, event, productFindingMethod) => {
    const gender = analytics?.product?.[0]?.productInfo?.gender;
    const { price, masterCategory } = recommendedProduct;

    return {
        event,
        effect: 'go to product page',
        category: {
            primaryCategory: 'upselling',
            subCategory: 'product page',
        },
        attributes: {
            position,
        },
        item: [
            {
                attributes: {
                    productFindingMethod,
                    crossSellItem: analytics?.product?.[0]?.productInfo?.productID,
                    sustainabilityFlag: analytics?.product?.[0]?.attributes.sustainabilityFlag,
                    badges: recommendedProduct?.badge,
                    discountPercent: price?.discountPercentage,
                    position,
                    saleFlag: price?.discountPercentage > 0 ? 'on sale' : 'full price',
                    stock: price?.availability === 'InStock' ? 'IN_STOCK' : false,
                },
                category: {
                    primaryCategory: masterCategory?.labelEN || masterCategory?.label,
                    subCategory1: masterCategory?.child?.labelEN || masterCategory?.child?.label,
                    subCategory2: masterCategory?.child?.child?.labelEN || masterCategory?.child?.child?.label,
                },
                price: {
                    basePrice: price?.originalPrice || price?.finalPrice,
                    discount: price?.discountPercentage || 0,
                    discountPrice: price?.originalPrice ? price?.originalPrice - price?.finalPrice : 0,
                    priceWithDiscountTax: price?.finalPrice,
                    currency: price?.currency?.label,
                },
                productInfo: {
                    designer: recommendedProduct?.designer,
                    designerID: recommendedProduct?.designerIdentifier,
                    name: recommendedProduct?.description,
                    productID: recommendedProduct?.partNumber,
                    ...(gender ? { gender } : {}),
                },
            },
            getItem(analytics)[0],
        ],
    };
};

export const trackAddToBag = (analytics, sizeSku, sizeSelectionLabel, quantity = 1, internationalSizes) => {
    const { category = {} } = analytics.page;
    return {
        event: 'add to cart',
        effect: 'update cart',
        category: {
            primaryCategory: category.primaryCategory,
            subCategory: category.subCategory1,
        },
        item: getItem(analytics, sizeSku, sizeSelectionLabel, true, quantity, internationalSizes),
    };
};

export const getItem = (analytics, sizeSku, sizeSelectionLabel, withPrice, quantity, internationalSizes) => {
    if (!analytics?.product?.[0]) return null;

    const { productInfo, category, attributes, price } = analytics?.product?.[0] || {};
    const { productID, productName, manufacturer, manufacturerID, gender } = productInfo;
    const { firstVisibleDate, productStock, preOrderFlag = false, badges = [], sustainabilityFlag, saleFlag } = attributes;
    const sku = productStock.find((sku) => sku.size === sizeSelectionLabel);

    return [
        {
            productInfo: {
                productID,
                productName,
                manufacturer,
                manufacturerID,
                ...(sizeSku ? { sku: sizeSku, size: sizeSelectionLabel, sizeInfo: parseSizeInfo(internationalSizes) } : {}),
                ...(gender ? { gender } : {}),
            },
            category,
            ...(quantity && { quantity }),
            attributes: {
                firstVisibleDate,
                preOrderFlag,
                ...(sku ? { stock: sku.stock } : {}),
                badges,
                sustainabilityFlag,
                saleFlag,
            },
            ...(withPrice ? { price: price } : {}),
        },
    ];
};

export const trackViewMoreNavigation = (analytics) => {
    const { category = {} } = analytics.page;
    return {
        event: 'upsell - similar products - to plp',
        effect: 'navigate',
        category: {
            primaryCategory: 'upselling',
            subCategory: category.subCategory1,
        },
        item: getItem(analytics),
    };
};

export const trackEmailUsCTA = (analytics, messageType) => {
    return {
        event: 'personal shopper click - email',
        effect: 'contact personal shopper',
        category: {
            primaryCategory: 'product page',
            subCategory: messageType,
        },
    };
};

export const trackCallUsCTA = (analytics) => {
    return {
        event: 'call customer care',
        effect: 'proceed calling the customer care',
        category: {
            primaryCategory: 'product page',
            subCategory: analytics?.page?.pageInfo?.pageName || '',
        },
    };
};

function parseSizeInfo(internationalSizes) {
    if (!internationalSizes) return null;
    let sizeInfo = {};
    internationalSizes.forEach((size) => {
        sizeInfo[size.name] = size.labels[0];
    });
    return sizeInfo;
}
